// #3505 — tells the user their domain pack may not have applied at project
// create.
//
// NewProjectModal creates the project, then applies a non-Generic pack with a
// separate PATCH /projects/:id/domain-pack. When that PATCH fails, the modal
// still navigates to the project (#2002), with
// ?notice=domain_pack_failed&pack=<value>. The notice sits on the Overview
// next to ProjectDomainPackCard, where the pack can be set.
//
// useDomainPackFailedNotice is called at the top of ProjectDashboardPage, so
// the state outlives the Overview tab's content (a tab switch must not lose
// it) and the URL is stripped before the project has loaded. The params are
// read once, at page mount, and removed straight away (replace), so a refresh,
// back or forward never shows the notice again. It then stays until
// dismissed: no timer, since a failure notice that hides itself is the silent
// failure again. Dismiss pattern: TierUpgradeBanner, Phase25RecoveryNotice
// (not SetupGateNotice, which auto-clears).

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PACK_LABELS, type DomainPackType } from './ProjectDomainPackCard';

export const DOMAIN_PACK_FAILED_NOTICE = 'domain_pack_failed';

export interface DomainPackFailedNoticeState {
  /** Display label of the intended pack, or null to name none. */
  packLabel: string | null;
}

function isDomainPackType(value: string): value is DomainPackType {
  return Object.prototype.hasOwnProperty.call(PACK_LABELS, value);
}

function readNotice(searchParams: URLSearchParams): DomainPackFailedNoticeState | null {
  if (searchParams.get('notice') !== DOMAIN_PACK_FAILED_NOTICE) return null;
  const pack = searchParams.get('pack');
  // Only a non-Generic pack from the enum is named; the raw param is never
  // shown. Generic is never a failed pack (the modal skips its PATCH).
  const named = pack !== null && isDomainPackType(pack) && pack !== 'generic';
  return { packLabel: named ? PACK_LABELS[pack] : null };
}

export function useDomainPackFailedNotice(projectId?: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy initializer, not an effect: StrictMode re-runs effects after the
  // URL has been stripped, but keeps this state. The notice records the
  // project it was raised for: the dashboard isn't remounted when only
  // :projectId changes, so A's notice must not show on B (R3 N2).
  const [raised, setRaised] = useState(() => {
    const notice = readNotice(searchParams);
    return notice ? { projectId, notice } : null;
  });

  // On another project, clear it (adjusting state during render, so B never
  // paints A's notice); coming back to A does not bring it back.
  if (raised && raised.projectId !== projectId) setRaised(null);
  const notice = raised && raised.projectId === projectId ? raised.notice : null;

  useEffect(() => {
    if (searchParams.get('notice') !== DOMAIN_PACK_FAILED_NOTICE) return;
    const next = new URLSearchParams(searchParams);
    next.delete('notice');
    next.delete('pack');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const dismiss = useCallback(() => setRaised(null), []);
  return { notice, dismiss };
}

export default function DomainPackFailedNotice({
  notice, onDismiss,
}: { notice: DomainPackFailedNoticeState | null; onDismiss: () => void }) {
  if (!notice) return null;

  const what = notice.packLabel ? `the ${notice.packLabel} domain pack` : 'its domain pack';

  return (
    <div
      role="alert"
      data-testid="domain-pack-failed-notice"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 14px',
        background: 'rgba(245, 158, 11, 0.10)', border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: 'var(--radius-sm, 8px)', fontFamily: 'var(--font-body)', fontSize: 13,
        color: 'var(--text)',
      }}
    >
      <span style={{ flex: 1 }}>
        Your project was created, but we couldn&apos;t confirm that {what} was applied. Check the
        Domain Pack card on this page.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss domain pack notice"
        data-testid="domain-pack-failed-notice-dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-secondary, #888)', fontSize: 16, lineHeight: 1, padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
